// Libraries
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, usePagination, useResponsive, useState} from '@supermove/hooks';
import {ProjectModel, MilestoneModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Datetime} from '@supermove/utils';

// App
import ErrorState from '@shared/design/components/EmptyState/ErrorState';
import Line from '@shared/design/components/Line';
import PaginationBar from '@shared/design/components/Pagination/PaginationBar';
import Table, {ColumnDefinitionType} from '@shared/design/components/Table';
import Text from '@shared/design/components/Text';
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import MilestoneAdKind from '@shared/modules/Milestone/enums/MilestoneAdKind';
import MilestoneKind from '@shared/modules/Milestone/enums/MilestoneKind';
import {ProjectBlockKindType} from '@shared/modules/Project/enums/ProjectBlockKind';
import SkeletonLoader from 'modules/App/components/SkeletonLoader';
import AdditionalMilestoneDataPopover from 'modules/Project/V2/Show/Blocks/components/AdditionalMilestoneDataPopover';
import ProjectBlockWrapper from 'modules/Project/V2/Show/Blocks/components/ProjectBlockWrapper';
import UtmDetailsComponent from 'modules/Project/V2/Show/Blocks/components/UtmDetailsComponent';

const OuterContainer = Styled.View<{isWideView: boolean}>`
  ${({isWideView}) => isWideView && `border: 1px solid ${colors.gray.border};`}
  ${({isWideView}) => !isWideView && `padding-bottom: 24px;`}
`;

const Container = Styled.View<{isWideView: boolean}>`
  background-color: ${colors.white};
  ${({isWideView}) => isWideView && `padding: 24px;`}
  width: 100%;
`;

const Header = Styled.View<{isWideView: boolean}>`
  ${({isWideView}) => !isWideView && `padding-left: 16px;`}
  ${({isWideView}) => !isWideView && `padding-top: 24px;`}
  ${({isWideView}) => !isWideView && `padding-bottom: 16px;`}
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const formatCurrency = (cents?: number) => {
  if (!cents) return 'None';
  return `$${(cents / 100).toFixed(2)}`;
};

const formatDate = (timestamp: string) => {
  // Use Datetime utility to handle UTC conversion
  const momentDate = Datetime.fromDatetime(timestamp);
  return momentDate.format('MM/DD/YY');
};

const formatTime = (timestamp: string) => {
  // Use Datetime utility to handle UTC conversion
  const momentDate = Datetime.fromDatetime(timestamp);
  return momentDate.format('h:mm A');
};

const SkeletonComponent = ({isWidget}: {isWidget?: boolean}) => {
  const responsive = useResponsive();
  const isWideView = responsive.desktop && !isWidget;
  return (
    <Container isWideView={isWideView}>
      <Header isWideView={isWideView}>
        <Text.Heading2>Milestones</Text.Heading2>
      </Header>
      <Space height={24} />
      <SkeletonLoader height={200} />
    </Container>
  );
};

const AdDetailsContainer = Styled.View`
  flex-direction: column;
`;

const getMilestonesColumnDefinition = (
  isWideView: boolean,
): ColumnDefinitionType<MilestoneModel>[] => {
  return [
    {
      flex: 2,
      minWidth: 120,
      headerLabel: 'Milestone',
      cellText: (milestone) => MilestoneKind.getLabel(milestone.kind),
      mobileOptions: {
        isInHeader: true,
        rank: 0,
      },
    },
    {
      flex: 2,
      minWidth: 120,
      headerLabel: 'Timestamp',
      cellText: (milestone) =>
        isWideView
          ? formatDate(milestone.timestamp)
          : `${formatDate(milestone.timestamp)} ${formatTime(milestone.timestamp)}`,
      secondary: isWideView
        ? {
            cellText: (milestone) => formatTime(milestone.timestamp),
          }
        : undefined,
      mobileOptions: {
        isInHeader: true,
        rank: 2,
      },
    },
    {
      flex: 3,
      minWidth: 150,
      headerLabel: 'Campaign',
      cellText: (milestone) => milestone.utmCampaign,
      secondary: {
        headerLabel: 'UTM Details',
        cellComponent: (milestone) => (
          <UtmDetailsComponent milestone={milestone} isWideView={isWideView} />
        ),
      },
    },
    {
      flex: 3,
      minWidth: 150,
      headerLabel: 'Landing URL',
      cellComponent: (milestone) => {
        if (!milestone.landingUrl) {
          return (
            <Text.Body style={{color: colors.gray.tertiary, fontStyle: 'italic'}}>None</Text.Body>
          );
        }
        return (
          <Text.Link onPress={() => window.open(milestone.landingUrl, '_blank')}>
            {milestone.landingUrl}
          </Text.Link>
        );
      },
    },
    {
      flex: 2,
      minWidth: 120,
      headerLabel: 'Ad Type',
      cellText: (milestone) => {
        const adKindLabel = MilestoneAdKind.getLabel(milestone.adKind);
        return milestone.adClickId ? `${adKindLabel} (${milestone.adClickId})` : adKindLabel;
      },
      secondary: {
        headerLabel: 'Cost',
        cellComponent: (milestone) => (
          <AdDetailsContainer>
            {milestone.adCost ? (
              <Text.Body>{formatCurrency(milestone.adCost)}</Text.Body>
            ) : (
              <Text.Body style={{color: colors.gray.tertiary, fontStyle: 'italic'}}>
                Unknown
              </Text.Body>
            )}
          </AdDetailsContainer>
        ),
      },
    },
    {
      flex: 2,
      minWidth: 120,
      headerLabel: 'Keywords',
      cellText: (milestone) => milestone.keywords?.join(', '),
      isHidden: isWideView,
    },
    {
      flex: 2,
      minWidth: 120,
      headerLabel: 'Device',
      cellText: (milestone) => milestone.device,
      isHidden: isWideView,
    },
    {
      flex: 2,
      minWidth: 120,
      headerLabel: 'OS',
      cellText: (milestone) => milestone.os,
      isHidden: isWideView,
    },
    {
      flex: 2,
      minWidth: 120,
      headerLabel: 'Browser',
      cellText: (milestone) => milestone.browser,
      isHidden: isWideView,
    },
    {
      flex: 1,
      minWidth: 50,
      maxWidth: 80,
      cellComponent: (milestone) => <AdditionalMilestoneDataPopover milestone={milestone} />,
      isHidden: !isWideView,
    },
  ];
};

const ProjectLeadInformationBlockContent = ({
  projectUuid,
  isWidget,
}: {
  projectUuid: string;
  isWidget?: boolean;
}) => {
  const pageSize = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const responsive = useResponsive();
  const isWideView = responsive.desktop && !isWidget;

  const {data, loading, error} = useQuery(ProjectLeadInformationBlock.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      projectUuid,
      pagination: {
        page: currentPage,
        resultsPerPage: pageSize,
      },
    },
  });

  const milestones = data?.project?.milestonesPaginatedList?.results || [];
  const paginationMetadata = data?.project?.milestonesPaginatedList?.paginationMetadata;

  const pagination = usePagination({
    currentPage,
    paginationMetadata,
    onChangePage: (page) => {
      setCurrentPage(page);
    },
  });

  if (error) {
    return (
      <OuterContainer isWideView={isWideView}>
        <ErrorState />
      </OuterContainer>
    );
  }

  return (
    <ErrorCatcher
      ErrorComponent={() => (
        <OuterContainer isWideView={isWideView}>
          <ErrorState />
        </OuterContainer>
      )}
    >
      <OuterContainer isWideView={isWideView}>
        <Container isWideView={isWideView}>
          <Header isWideView={isWideView}>
            <Text.Heading2>Milestones</Text.Heading2>
          </Header>
        </Container>
        <Line />
        <Container isWideView={isWideView}>
          <Table
            columnDefinitions={getMilestonesColumnDefinition(isWideView)}
            items={milestones}
            emptyStateText='No milestones to display'
            isLoading={loading}
            hasBorder={isWideView}
            isDesktopCard={isWidget}
          />

          <React.Fragment>
            <Space height={16} />
            <PaginationBar pagination={pagination} />
          </React.Fragment>
        </Container>
      </OuterContainer>
    </ErrorCatcher>
  );
};

const ProjectLeadInformationBlock = ({
  project,
  handleSetPositionY,
  layoutKey,
  index,
  projectBlockKind,
  isWidget,
}: {
  project: ProjectModel;
  handleSetPositionY?: () => void;
  layoutKey?: string;
  index: number;
  projectBlockKind: ProjectBlockKindType;
  isWidget?: boolean;
}) => {
  return (
    <ProjectBlockWrapper
      index={index}
      query={ProjectLeadInformationBlock.query}
      queryVariables={{
        projectUuid: project.uuid,
        pagination: {
          page: 1,
          resultsPerPage: 5,
        },
      }}
      layoutKey={layoutKey}
      handleSetPositionY={handleSetPositionY}
      SkeletonComponent={() => <SkeletonComponent isWidget={isWidget} />}
      projectBlockKind={projectBlockKind}
      isWidget={isWidget}
      style={{}} // Remove the default styling on mobile
    >
      {({data}: {data: {project: ProjectModel}; refetch: () => void}) => {
        const {project} = data;
        return (
          <ProjectLeadInformationBlockContent projectUuid={project.uuid} isWidget={isWidget} />
        );
      }}
    </ProjectBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectLeadInformationBlock.fragment = gql`
  fragment ProjectLeadInformationBlock on Project {
    id
    uuid
  }
`;

ProjectLeadInformationBlock.query = gql`
  ${usePagination.fragment}
  query ProjectLeadInformationBlock($projectUuid: String!, $pagination: PaginationInput!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      uuid
      milestonesPaginatedList(pagination: $pagination) {
        results {
          id
          kind
          timestamp
          utmCampaign
          utmMedium
          utmSource
          utmContent
          utmTerm
          landingUrl
          adKind
          adClickId
          adCost
          keywords
          device
          os
          browser
        }
        paginationMetadata {
          ...usePagination
        }
      }
    }
  }
`;

export default ProjectLeadInformationBlock;
